import { LoggerProvider } from '../../logger';
import Container from 'typedi';
import { WindowToken } from '../../../injection-tokens';
import { ILogger } from '@sparkware/uc-sdk-core';

export interface IIntersectionObserverOptions {
    root?: HTMLElement;
    rootMargin?: string;
    threshold?: number;
}

export class IntersectionObserverManager {
    private _intersectionObserverInstance: IntersectionObserver;
    private _callbacks: IntersectionObserverCallback[] = [];

    private _targetElement: HTMLElement;
    private readonly _logger: ILogger;
    private readonly _window: Window;

    constructor(loggerType: string, options?: IIntersectionObserverOptions) {
        this._logger = Container.get(LoggerProvider).getLogger(
            loggerType || 'IntersectionObserverManager',
        );
        this._window = Container.get(WindowToken);

        this._intersectionObserverInstance = this._create(options);
    }

    public ObserveElement = (
        target: HTMLElement,
        callback: IntersectionObserverCallback,
        options?: IIntersectionObserverOptions,
    ): void => {
        if (!this._intersectionObserverInstance) {
            this._intersectionObserverInstance = this._create(options);
        }

        if (!this._intersectionObserverInstance) {
            this._logger.warn(
                '[ObserveElement]: Intersection observer API is not available, callbacks will be called right away',
            );
            callback([], null);
            return;
        }

        this._targetElement = target;
        this._logger.debug(
            `[ObserveElement]: Observing the following element, "${target?.id || 'undefined'}"`,
        );

        this._callbacks.push(callback);
        this._intersectionObserverInstance.observe(target);
    };

    public Disconnect = () => {
        if (this._intersectionObserverInstance) {
            if (this._targetElement) {
                this._intersectionObserverInstance.unobserve(this._targetElement);
            }
            this._intersectionObserverInstance.disconnect();
            this._intersectionObserverInstance = null;
        }

        this._callbacks.pop();
    };

    private _create(options?: IIntersectionObserverOptions): IntersectionObserver | null {
        try {
            if (!this._window.IntersectionObserver) {
                this._logger.warn('[_create]: IntersectionObserver API does not exist.');
                return null;
            }

            return new IntersectionObserver(
                (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
                    this._callbacks.forEach((callback) => {
                        callback(entries, observer);
                    });
                },
                options,
            );
        } catch (e) {
            this._logger.warn(
                '[_create]: Failed to create IntersectionObserver. Will use the fallback mechanism...',
                e,
            );
            return null;
        }
    }
}
